import { useEffect, useRef, useState, type ReactNode } from 'react'
import AppHeader from './AppHeader'
import AdminNav, { type AdminArea } from './AdminNav'
import { useIsDesktop } from './useIsDesktop'
import { useProfileIdentity } from '../../lib/useProfileIdentity'

function MenuIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M3 6h18M3 12h18M3 18h18" />
    </svg>
  )
}

/**
 * The administrator workspace's frame: header, the administration
 * navigation (a drawer below `lg`), and the main region with its title and
 * footer. Shared by every administration screen, so the drawer's focus and
 * inert handling live in one place.
 */
export default function AdminLayout({
  current,
  title,
  subtitle,
  titleAddon,
  children,
}: {
  current: AdminArea
  title: string
  subtitle?: string
  // Shown beside the title, such as an information button.
  titleAddon?: ReactNode
  children: ReactNode
}) {
  const identity = useProfileIdentity()
  const [navOpen, setNavOpen] = useState(false)
  const navToggleRef = useRef<HTMLButtonElement>(null)
  const wasNavOpenRef = useRef(false)
  const isDesktop = useIsDesktop()

  // Returns focus to the toggle once the drawer actually closes, rather
  // than from the click handler that asks it to: main is still inert at
  // that point, since the DOM has not caught up with the state update yet,
  // and focusing an element inside an inert ancestor silently fails. An
  // effect runs after the commit that removes inert; a same-tick call, or
  // even a requestAnimationFrame guess at one, does not reliably.
  useEffect(() => {
    if (wasNavOpenRef.current && !navOpen) navToggleRef.current?.focus()
    wasNavOpenRef.current = navOpen
  }, [navOpen])

  return (
    <div className="flex h-dvh flex-col bg-surface text-ink print:block print:h-auto">
      <a
        href="#admin-main"
        className="sr-only focus:not-sr-only focus:absolute focus:z-50 focus:m-2 focus:rounded focus:bg-canvas focus:px-4 focus:py-2 focus:font-semibold"
      >
        Skip to main content
      </a>

      <div className="print:hidden">
        <AppHeader initials={identity?.initials ?? ''} />
      </div>

      <div className="relative flex flex-1 overflow-hidden print:block print:overflow-visible">
        <AdminNav current={current} open={navOpen} onClose={() => setNavOpen(false)} />

        <main
          id="admin-main"
          // Focusable, so the scrolling region is reachable from the keyboard even
          // when nothing inside it is interactive.
          tabIndex={0}
          // The open drawer's backdrop covers this region below `lg`,
          // including the toggle button that opened it; inert keeps focus
          // from reaching those hidden controls instead of only the drawer
          // trapping it.
          inert={navOpen && !isDesktop ? true : undefined}
          // Positioned, so the screen-reader-only text inside (absolutely
          // placed) belongs to this scroll area. Without it that text belongs
          // to the frame around it, which then scrolls far past the page, and
          // a jump to a section leaves the frame shifted with a gap under it.
          className="relative flex-1 overflow-y-auto p-4 sm:p-6 lg:p-8 print:overflow-visible print:p-0"
        >
          <div className="mx-auto flex max-w-[1400px] flex-col gap-5 lg:gap-6">
            <div className="flex items-center gap-3">
              <button
                ref={navToggleRef}
                type="button"
                onClick={() => setNavOpen(true)}
                aria-label="Open administration menu"
                className="flex size-11 flex-none items-center justify-center rounded-control border border-line-strong lg:hidden print:hidden"
              >
                <MenuIcon />
              </button>
              <div>
                <div className="flex items-center gap-0.5">
                  <h1 className="text-h2 font-extrabold">{title}</h1>
                  {titleAddon}
                </div>
                {subtitle && <p className="mt-1 text-small text-ink-muted">{subtitle}</p>}
              </div>
            </div>

            {children}
          </div>
        </main>
      </div>
    </div>
  )
}
